import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { GET_ALL_TRANSPORT } from '../../../query/transport-activity'
import { useQuery } from '@apollo/client'
import { ITransport } from './types'
import { getTransportVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { EXPORT_TRANSPORT_QUERY } from '../../../query/export-transport'

import './index.scss'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'

const TransportActivity: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<ITransport[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [transportVars, setTransportVars] = useState<any>(getTransportVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, refetch } = useQuery(GET_ALL_TRANSPORT, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getTransportVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.transportActivities.pageInfo.endCursor)
    setStartCursor(data.transportActivities.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/food/transport-activity/transport',
      'transportActivities',
      'transportActivity'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setTransportVars({
          ...filterEnums(tableSearch, getTransportVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getTransportVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'TransportActivities',
      query: EXPORT_TRANSPORT_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: '/food/transport-activity',
                title: 'Transport Activity Table',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Transport Activity</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={() => navigate('/food/transport-activity/transport?itemId=new')}>
            Add Transport
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={transportVars || getTransportVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default TransportActivity
